import { ref, computed, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import ApiCNet from '@/api/ApiCNet';
import Consts from '@/api/Consts';

import { showErrorMessage } from '@/components/common/errorMessage';

import type {
	CcmCnetListItem,
	CcmCnetRealGridpoint,
	CcmOtherGridpointDisplay,
} from '@/components/map/cnetConstructorMode/types/ccmTypes';


type UseCnetOtherGridsResult = {
	showOtherGrids: Ref<boolean>;
	otherGridsList: Ref<CcmCnetListItem[]>;
	selectedOtherGridsKeys: Ref<number[]>;
	otherGridsLoading: Ref<boolean>;
	otherGridpoints: ComputedRef<CcmOtherGridpointDisplay[]>;
	resetOtherGrids: () => void;
};

const useCnetOtherGrids = (editId: Ref<number>): UseCnetOtherGridsResult => {
	const showOtherGrids = ref(false);
	const otherGridsList = ref<CcmCnetListItem[]>([]);
	const selectedOtherGridsKeys = ref<number[]>([]);
	const otherGridsDetails = ref<Map<number, CcmCnetRealGridpoint[]>>(new Map());
	const otherGridsLoading = ref(false);

	const loadOtherGridsList = (currentEditId: number): void => {
		otherGridsLoading.value = true;
		ApiCNet.cNetListGetList(currentEditId, response => {
			if (response.status === 'ok') {
				otherGridsList.value = response.cnetList ?? [];
				selectedOtherGridsKeys.value = [];
			} else {
				showErrorMessage(response.errors);
			}
			otherGridsLoading.value = false;
		});
	};

	const fetchCnetDetails = (id: number): void => {
		if (otherGridsDetails.value.has(id)) {
			return;
		}
		ApiCNet.cNetRealDataGet(id, data => {
			if (data.status === 'ok') {
				otherGridsDetails.value.set(id, data.gridpoints ?? []);
			} else {
				showErrorMessage(data.errors);
			}
		});
	};

	const resetOtherGrids = (): void => {
		otherGridsList.value = [];
		selectedOtherGridsKeys.value = [];
		otherGridsDetails.value = new Map();
		if (showOtherGrids.value) {
			loadOtherGridsList(editId.value);
		}
	};

	watch(showOtherGrids, value => {
		if (value) {
			loadOtherGridsList(editId.value);
		}
	});

	watch(selectedOtherGridsKeys, keys => {
		keys.forEach(id => fetchCnetDetails(id));
	});

	const otherGridpoints = computed((): CcmOtherGridpointDisplay[] => {
		const result: CcmOtherGridpointDisplay[] = [];
		selectedOtherGridsKeys.value.forEach(id => {
			const detailGridpoints = otherGridsDetails.value.get(id);
			if (detailGridpoints) {
				detailGridpoints.forEach(gridpoint => {
					result.push({
						label: gridpoint.label,
						lon: gridpoint.lon_float,
						lat: gridpoint.lat_float,
						color: Consts.getColor(id, Consts.CHART_COLORS),
					});
				});
			}
		});
		return result;
	});

	return {
		showOtherGrids,
		otherGridsList,
		selectedOtherGridsKeys,
		otherGridsLoading,
		otherGridpoints,
		resetOtherGrids,
	};
};

export {
	useCnetOtherGrids,
};
